import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Code, Server, TrendingUp, Users } from 'lucide-react';

const timeline = [
  {
    period: '2011 - 2013',
    role: 'PHP Web Developer',
    company: 'Game Platform',
    description: 'Started as a PHP developer working on a game platform serving 30-50k concurrent users. Built backend scripts for data processing and crafted frontend experiences that kept players engaged. This was my first taste of high-traffic systems running on bare metal — over 200 servers in self-owned racks at rented datacenter footprints.',
    icon: Code,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
  },
  {
    period: '2013 - 2017',
    role: 'Senior IT Engineer',
    company: 'Game Platform',
    description: 'Evolved into infrastructure work, managing the entire hardware stack. Learned the ins and outs of running large-scale systems on physical servers — no cloud abstractions, just raw performance and reliability.',
    icon: Server,
    color: 'text-emerald-500',
    bg: 'bg-emerald-500/10',
  },
  {
    period: '2017 - 2018',
    role: 'Head of IT',
    company: 'Game Platform',
    description: 'Led the IT department, overseeing infrastructure strategy and team coordination.',
    icon: Users,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
  },
  {
    period: '2018 - 2025',
    role: 'Head of IT',
    company: 'Customer Data Platform',
    description: 'Built a customer data platform from scratch in a small team of 4. As the sole infrastructure owner, I architected everything — from initial cloud hosting to a full Kubernetes migration, scaling from sub-1k RPM to over 100k RPM. Eventually moved from cloud to rented hardware virtualization, running over 150 VMs across 30 physical nodes. This journey taught me that infrastructure isn\'t just about scaling up — it\'s about choosing the right architecture at each stage of growth.',
    icon: TrendingUp,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
  },
];

export function About() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
      <div className="space-y-12">
        {/* Story Section */}
        <div className="max-w-3xl">
          <div className="space-y-4">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight">About Me</h2>
            <p className="text-muted-foreground text-lg leading-relaxed">
              I've been passionate about PHP development since I was young, and that early interest in coding naturally led me to Linux server hosting. What started as tinkering with servers in my spare time became a career building and scaling infrastructure for high-traffic platforms.
            </p>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Over the years, I've worked with everything from bare-metal racks in datacenters to modern Kubernetes clusters. I've learned that there's no one-size-fits-all solution — sometimes you need 200 physical servers, sometimes 150 VMs, and sometimes just the right architecture to handle 100k+ requests per minute.
            </p>
          </div>
        </div>

        {/* Horizontal Timeline */}
        <div className="space-y-6">
          <h3 className="text-2xl font-bold tracking-tight">My Journey</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {timeline.map((item) => {
              const Icon = item.icon;

              return (
                <div key={item.period}>
                  <Card className="border-border/50 hover:border-primary/30 transition-all duration-300 h-full">
                    <CardContent className="p-4">
                      <div className="space-y-3">
                        <div className={`${item.bg} w-10 h-10 rounded-lg flex items-center justify-center mx-auto`}>
                          <Icon className={`w-5 h-5 ${item.color}`} />
                        </div>
                        <div className="text-center space-y-1">
                          <h4 className="font-bold text-sm">{item.role}</h4>
                          <p className="text-xs text-muted-foreground">{item.company}</p>
                          <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-md inline-block">
                            {item.period}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-4">
                          {item.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

